import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axiosConfig';

const POLL_INTERVAL_MS = 60 * 1000;      // Check session every 60s
const ACTIVITY_DEBOUNCE_MS = 30 * 1000; // Min time between renewals
const RENEW_THRESHOLD_MINS = 25;         // Renew if <25 min left

export default function useSessionPoller() {
  const {
    isAuthenticated,
    getAccessToken,
    expiresAt,
    logout,
    refreshSession,
    isRefreshRevoked,
  } = useAuth();

  const redirectToLogin = useCallback(async (reason = 'Your session has expired. Please log in again.') => {
    sessionStorage.setItem('sessionEndedMessage', reason);
    await logout();
    window.location.href = '/login';
  }, [logout]);

  const intervalRef = useRef(null);
  const lastRenewalRef = useRef(0);
  const isRenewingRef = useRef(false);
  const consecutiveCheckFailsRef = useRef(0);

  // Silent token renewal
  const renewToken = useCallback(async () => {
    if (isRenewingRef.current) return;

    const now = Date.now();
    if (now - lastRenewalRef.current < ACTIVITY_DEBOUNCE_MS) return;

    if (expiresAt) {
      const minutesLeft = (expiresAt - now / 1000) / 60;
      if (minutesLeft > RENEW_THRESHOLD_MINS) return;
    }

    isRenewingRef.current = true;
    lastRenewalRef.current = now;

    try {
      await refreshSession();
      console.log('[Poller] Token silently renewed on user activity');
    } catch {
      // Silent fail — session check will catch real expiry
    } finally {
      isRenewingRef.current = false;
    }
  }, [expiresAt, refreshSession]);

  // Session validity check
  const checkSession = useCallback(async () => {
    if (!getAccessToken()) return;

    try {
      await api.get('/auth/session-status');
      consecutiveCheckFailsRef.current = 0;
    } catch (err) {
      if (err?.response?.status === 401) {
        consecutiveCheckFailsRef.current += 1;
        if (consecutiveCheckFailsRef.current >= 2) {
          console.log('[Poller] Session revoked after consecutive failures, logging out...');
          consecutiveCheckFailsRef.current = 0;
          await redirectToLogin();
        }
      }
    }
  }, [getAccessToken, redirectToLogin]);

  // Redirect immediately if refresh token is revoked (e.g. force-logout from server)
  useEffect(() => {
    if (isRefreshRevoked && isAuthenticated) {
      redirectToLogin();
    }
  }, [isRefreshRevoked, isAuthenticated, redirectToLogin]);

  // Activity listener — renew token on user interaction
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleActivity = () => renewToken();

    // Listen to these user activity events
    const events = [
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ];

    events.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    return () => {
      events.forEach(event =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [isAuthenticated, renewToken]);

  // Session validity polling
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check immediately
    checkSession();

    // Then poll on interval
    intervalRef.current = setInterval(checkSession, POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, checkSession]);
}
